import type { DriverRow } from "@/types/driver";

/**
 * {@link ColumnSort.columnKey} of the built-in Group column. Every other key is a workspace tag id.
 *
 * Group is a core roster field rather than a workspace tag, so it cannot be read out of
 * {@link DriverRow.tags} the way tag columns are — but it shares the column header's sort control,
 * so it shares this state.
 */
export const GROUP_SORT_COLUMN_KEY = "group";

export type ColumnSort = { columnKey: string; direction: "asc" | "desc" };

export function applyTagFilters(drivers: DriverRow[], tagFilters: Record<string, string[]>): DriverRow[] {
  const activeFilters = Object.entries(tagFilters).filter(([, values]) => values.length > 0);
  if (activeFilters.length === 0) return drivers;

  return drivers.filter((d) =>
    activeFilters.every(([tagId, allowedValues]) => {
      const values = d.tags[tagId];
      return values != null && values.some((v) => allowedValues.includes(v));
    }),
  );
}

/** Drivers with no value for the sorted column collapse to the empty string, so they lead ascending. */
function sortValue(driver: DriverRow, columnKey: string): string {
  if (columnKey === GROUP_SORT_COLUMN_KEY) return driver.group ?? "";
  return driver.tags[columnKey]?.[0] ?? "";
}

export function applyColumnSort(drivers: DriverRow[], sort: ColumnSort): DriverRow[] {
  return [...drivers].sort((a, b) => {
    const cmp = sortValue(a, sort.columnKey).localeCompare(sortValue(b, sort.columnKey));
    return sort.direction === "desc" ? -cmp : cmp;
  });
}
